import { useState } from 'react';
import { FileFormatEnum } from 'types/generated/client';
import { useModal } from 'hooks/useModal';
import type { PlayerProfilePhoto } from 'screens/PlayerProfile/types';
import CardsSection from 'components/CardSection';
import MediaLightbox from 'components/MediaLightbox';
import ProfileSection from '../ProfileSection';

interface Props {
  photos: PlayerProfilePhoto[];
  isViewerProfile: boolean;
  onEdit?: () => void;
}

export default function ProfilePhotoGallery({ photos, isViewerProfile, onEdit }: Props) {
  const { isOpen, openModal, closeModal } = useModal();
  const [currentIndex, setCurrentIndex] = useState(0);

  const openAt = (index: number) => {
    setCurrentIndex(index);
    openModal();
  };

  return (
    <ProfileSection
      title="Photo gallery"
      isViewerProfile={isViewerProfile}
      onEdit={onEdit}
      isEmpty={!photos.length}
      emptyLabel="Add more photos"
      emptySize="lg"
    >
      {/* Partial card at the mobile breakpoint so the strip reads as scrollable;
          three 228px tiles fill the desktop column, per the design. */}
      <CardsSection
        data={photos}
        gap="0.5rem"
        visibleCards={{ base: 1.5, xs: 1.5, sm: 2.5, md: 3, lg: 3 }}
        renderCard={(photo, index) => (
          <button
            type="button"
            onClick={() => openAt(index)}
            aria-label={`Open photo ${index + 1}`}
            className="block w-full"
          >
            {photo.fileFormat === FileFormatEnum.Video ? (
              <video
                src={photo.url}
                className="aspect-square w-full rounded-xl object-cover"
                muted
                playsInline
              />
            ) : (
              <img
                src={photo.url}
                alt=""
                loading="lazy"
                className="aspect-square w-full rounded-xl object-cover"
              />
            )}
          </button>
        )}
      />

      {isOpen && (
        <MediaLightbox
          items={photos.map((photo) => ({ url: photo.url, format: photo.fileFormat }))}
          index={currentIndex}
          onIndexChange={setCurrentIndex}
          onClose={() => closeModal(true)}
        />
      )}
    </ProfileSection>
  );
}
